/**
 * «Мои задачи» — сводный список по всем проектам, куда человек допущен.
 *
 * Сервис отдаёт задачи одной ручкой, но статус у задачи — это колонка её
 * проекта, а колонки лежат отдельно. Поэтому список догружает колонки тех
 * проектов, которые в нём действительно встретились: спрашивать про все
 * проекты подряд значит платить за то, чего на экране нет.
 */
import { issueApi } from '@/api'
import { toRow, type IssueRow } from '@/features/issues/api'
import { listColumns, type ProjectColumn } from '@/features/projects/api'

import type { V1StatusCategory } from '@/api/clients/tracker-issue'

/** Отбор экрана — он же три чипа макета */
export type MyFilter = 'assigned' | 'watching' | 'overdue'

/** Задача сводного списка: строка плюс проект, по которому её группируют */
export interface MyIssue extends IssueRow {
  projectId: string
  /** Префикс ключа — он же ключ проекта: `SALES-214` → `SALES` */
  projectKey: string
}

const withProject = (row: IssueRow, projectId: string): MyIssue => ({
  ...row,
  projectId,
  projectKey: row.key.split('-')[0] ?? '',
})

/**
 * Размер страницы. Сотня — не наш выбор, а потолок сервиса: `boundPage`
 * молча срезает всё, что больше, и просить тысячу бессмысленно.
 */
const PAGE = 100

/** По чему список умеет сортировать — ровно то, что принимает сервер */
export type MySortField = 'due_date' | 'priority' | 'updated_at' | 'key'

/**
 * Отбор и порядок уходят на сервер, а не считаются здесь: список приезжает
 * страницами, и клиентский отбор врал бы на всём, что осталось за первой.
 */
export interface MyQuery {
  projectIds?: string[]
  statusCategories?: V1StatusCategory[]
  /** Срок не позже этого момента — им спрашивают «что горит на неделе» */
  dueBefore?: string
  sortBy?: MySortField
  sortDesc?: boolean
}

/**
 * Страницы дочитываются до конца, а не берётся первая.
 *
 * Экран не листается: он группирует задачи по проектам и считает по ним чипы
 * «назначено / наблюдаю / просрочено». Оборванная на первой странице выдача
 * врала бы дважды — и списком, и счётчиками, — причём молча: у кого задач
 * меньше сотни, тот разницы не увидит вовсе. Сортировка здесь по умолчанию
 * «по сроку», так что за границу уезжали как раз свежие задачи без срока
 * (DEV-1640).
 */
export const listMine = async (
  includeWatched: boolean,
  query: MyQuery = {},
): Promise<MyIssue[]> => {
  const issues: MyIssue[] = []

  for (let page = 1; ; page += 1) {
    // Клиент сгенерирован с позиционными аргументами, и порядок здесь —
    // единственное, что связывает поле фильтра с его местом в запросе. Сдвиг на
    // одну позицию превратил бы «срок не позже» в «срок не раньше» молча,
    // поэтому URL закреплён тестом.
    const { data } = await issueApi.issueServiceListMyIssues(
      includeWatched,
      true,
      String(page),
      String(PAGE),
      undefined, // filterAssigneeIds — исполнитель здесь всегда сам запросивший
      undefined, // filterLabelIds
      undefined, // filterTypes
      undefined, // filterPriorities
      undefined, // filterColumnIds
      undefined, // filterSearch
      undefined, // filterParentId
      undefined, // filterOnlyOpen
      query.dueBefore,
      query.projectIds?.length ? query.projectIds : undefined,
      query.statusCategories?.length ? query.statusCategories : undefined,
      undefined, // filterDueAfter
      undefined, // filterAncestorId
      undefined, // filterReporterIds — исполнитель здесь сам запросивший, автор не спрашивается
      query.sortBy,
      query.sortDesc,
    )

    issues.push(
      ...(data.issues ?? []).map((issue) => withProject(toRow(issue), issue.projectId ?? '')),
    )

    // Счётчик приходит строкой: в protobuf это uint64, в number он не влезает.
    if (issues.length >= Number(data.pageInfo?.total ?? 0)) return issues

    // Пустая страница при недобранном итоге — повод остановиться, а не крутить
    // цикл: значит выдача разошлась со счётчиком, и следующий заход вернёт то
    // же самое.
    if (!data.issues?.length) return issues
  }
}

/** Просрочено — срок раньше сегодняшнего дня; сегодняшний срок ещё не долг. */
export const isOverdue = (issue: MyIssue, now = new Date()): boolean => {
  if (!issue.dueDate) return false

  const due = new Date(issue.dueDate)

  if (Number.isNaN(due.getTime())) return false

  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime()

  return due.getTime() < startOfToday
}

/** Группа списка: проект и его задачи */
export interface MyGroup {
  projectKey: string
  issues: MyIssue[]
}

/**
 * Группировка по проекту. Порядок групп задаёт порядок проектов в сайдбаре —
 * человек уже привык к нему, и второй порядок на соседнем экране только сбивал
 * бы.
 */
export const groupByProject = (issues: MyIssue[], order: string[]): MyGroup[] => {
  const groups = new Map<string, MyIssue[]>()

  for (const issue of issues) {
    groups.set(issue.projectKey, [...(groups.get(issue.projectKey) ?? []), issue])
  }

  const rank = (key: string) => {
    const at = order.indexOf(key)

    return at === -1 ? order.length : at
  }

  return [...groups.entries()]
    .map(([projectKey, rows]) => ({ projectKey, issues: rows }))
    .sort((a, b) => rank(a.projectKey) - rank(b.projectKey))
}

/** Колонки проектов, встретившихся в списке — по ним рисуются пилюли статусов. */
export const loadColumns = async (
  projectIds: string[],
): Promise<Record<string, ProjectColumn[]>> => {
  const unique = [...new Set(projectIds.filter(Boolean))]
  const loaded = await Promise.all(
    unique.map(async (id) => [id, await listColumns(id)] as const),
  )

  return Object.fromEntries(loaded)
}
